import {
	ActorType,
	type Browser,
	type Event,
	type EventMappings,
	type Page,
	type Track,
	type WorkflowId,
} from '@1debit/chime-schemas/typescript_web_analytics';
import hyperid from 'hyperid';
import ClientCookies from 'js-cookie';
import UAParser from 'ua-parser-js';

import { isAppEmbedded } from './utils/isAppEmbedded';

import packageJson from '../../package.json' with { type: 'json' };

// Explicitly define what the client can override
export type AppEventContext = Pick<Event, 'app' | 'third_party_identifiers' | 'referral'> & {
	device?: {
		name: string;
	};
	browser?: Pick<Browser, 'is_embedded' | 'name'>;
};

export type UserProperties = {
	email?: string;
};

export type EventPayload = {
	events: ReadonlyArray<Event>;
};

export enum EventType {
	Track = 'EVENT_TYPE_TRACK',
}

export type EventData = {
	name: Track;
	properties: EventMappings[Track];
	type: EventType;
};

export type AnalyticsEvent = {
	data: EventData;
	id: string;
	timestamp: string;
};

export const MAX_EVENT_BATCH_SIZE = 10;
const UNKNOWN_FIELD = 'Unknown';
const USER_ID_COOKIE = 'chime_user_id';

export class EventRouterService {
	private deviceId: string;
	private userId = 0;
	private pageId?: string;
	private userProperties?: UserProperties;
	private analyticsEventsList: Event[] = [];
	private trackEventContext: Event;

	constructor(deviceId: string) {
		this.deviceId = deviceId;
		this.trackEventContext = {};
		const cookieUserId = ClientCookies.get(USER_ID_COOKIE);
		if (cookieUserId) {
			this.userId = parseInt(cookieUserId);
		}
	}

	public updateSessionId(sessionId: string): void {
		this.trackEventContext = {
			...this.trackEventContext,
			session_id: sessionId,
		};
	}

	public updateContext(appEventContext?: AppEventContext): void {
		const { browser, os, device, ua: userAgent } = UAParser(window.navigator.userAgent);
		this.trackEventContext = {
			...this.trackEventContext,
			...appEventContext,
		};

		this.trackEventContext.device = {
			manufacturer: device.vendor || UNKNOWN_FIELD,
			model: device.model || UNKNOWN_FIELD,
			...appEventContext?.device,
		};

		this.trackEventContext.page = {
			original_referrer_url: document.referrer,
		};

		this.trackEventContext.browser = {
			user_agent: userAgent,
			name: browser.name || UNKNOWN_FIELD,
			is_embedded: isAppEmbedded(),
			...appEventContext?.browser,
		};

		this.trackEventContext.os = {
			name: os.name || UNKNOWN_FIELD,
			version: os.version || UNKNOWN_FIELD,
		};
	}

	public setUserData(userId: number, userProperties?: UserProperties): void {
		this.userId = userId;
		ClientCookies.set(USER_ID_COOKIE, userId.toString(), { sameSite: 'Lax', secure: true, expires: 365 });
		this.userProperties = userProperties;
	}

	public removeUserData(): void {
		this.userId = 0;
		ClientCookies.remove(USER_ID_COOKIE);
		this.userProperties = undefined;
	}

	public updatePageId(pageId: string) {
		this.pageId = pageId;
	}

	public addWorkflow(workflowId: WorkflowId): void {
		this.trackEventContext.workflow = {
			ids: [...(this.trackEventContext.workflow?.ids ?? []), workflowId],
		};
	}

	public removeWorkflow(workflowId: WorkflowId): boolean {
		let removed = false;
		const ids = this.trackEventContext.workflow?.ids?.filter(id => {
			if (id === workflowId) {
				removed = true;
				return false;
			}
			return true;
		});
		this.trackEventContext.workflow = { ids };
		return removed;
	}

	private applyContextToEvent(event: Event): Event {
		return {
			...event,
			page: {
				...event.page,
				original_referrer_url: this.trackEventContext.page?.original_referrer_url,
			},
			app: this.trackEventContext.app,
			browser: this.trackEventContext.browser,
			device: this.trackEventContext.device,
			os: this.trackEventContext.os,
			referral: this.trackEventContext.referral,
			session_id: this.trackEventContext.session_id,
			third_party_identifiers: this.trackEventContext?.third_party_identifiers,
			workflow: { ids: this.trackEventContext.workflow?.ids ?? [] },
		};
	}

	private getPageContext(): Page {
		return {
			path: window.location.pathname,
			referrer_url: document.referrer,
			search: window.location.search,
			title: document.title,
			url: window.location.href,
		};
	}

	private createEvent(analyticsEvent: AnalyticsEvent): Event {
		const event: Event = {
			library: {
				name: '@1debit/analytics',
				version: packageJson.version,
			},
			user_id: this.userId,
			anonymous_id: this.deviceId,
			device_id: this.deviceId,
			page_id: this.pageId ?? UNKNOWN_FIELD,
			timestamp: analyticsEvent.timestamp,
			page: this.getPageContext(),
			email: this.userProperties?.email,
			json_payload: analyticsEvent.data.properties,
			fully_qualified_schema_name: analyticsEvent.data.name,
			event_id: analyticsEvent.id,
			sent_at: analyticsEvent.timestamp,
			actor_type: this.userId ? ActorType.ACTOR_TYPE_USER : ActorType.ACTOR_TYPE_VISITOR,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			locale: navigator.language,
		};
		return event;
	}

	public createAndAddEvent(eventData: EventData) {
		if (this.analyticsEventsList.length >= MAX_EVENT_BATCH_SIZE) {
			return false;
		}

		const analyticsEvent: AnalyticsEvent = {
			data: eventData,
			id: hyperid().uuid,
			timestamp: new Date().toISOString(),
		};

		const event = this.createEvent(analyticsEvent);

		this.analyticsEventsList.push(event);
		return true;
	}

	public addEventToRetry(event: Event): boolean {
		if (this.analyticsEventsList.length >= MAX_EVENT_BATCH_SIZE) {
			return false;
		}

		this.analyticsEventsList.push(event);
		return true;
	}

	public shouldSendEvents(): boolean {
		return this.analyticsEventsList.length > 0;
	}

	public createPayloadObject(): EventPayload {
		return { events: this.analyticsEventsList.map(event => this.applyContextToEvent(event)) };
	}

	public clearEvents(): void {
		this.analyticsEventsList = [];
	}
}
